import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COURSES } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function Batches() {
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", course: COURSES[0], trainer: "", start_date: "", end_date: "" });

  const load = async () => {
    const { data } = await api.get("/batches"); setBatches(data);
  };
  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/batches", form);
      toast.success("Batch created");
      setOpen(false);
      setForm({ name: "", course: "", trainer: "", start_date: "", end_date: "" });
      load();
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete batch?")) return;
    await api.delete(`/batches/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="batches-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1">Admin</div>
          <h1 className="font-heading text-3xl font-bold text-[#0F172A]">Batches</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-batch-button" className="bg-[#0F2E4A] hover:bg-[#174169] text-white">
              <Plus className="w-4 h-4 mr-1.5" /> Create Batch
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Batch</DialogTitle></DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4">
              <Field label="Batch Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="batch-name" />
              <div>
                <Label className="text-xs font-semibold text-[#475569]">Course *</Label>
                <Select value={form.course} onValueChange={(v) => setForm({ ...form, course: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="batch-course"><SelectValue placeholder="Select course" /></SelectTrigger>
                  <SelectContent>
                    {COURSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Field label="Trainer" value={form.trainer} onChange={(v) => setForm({ ...form, trainer: v })} required testid="batch-trainer" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Start Date" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} required testid="batch-start" />
                <Field label="End Date" type="date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testid="batch-end" />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-[#0F2E4A] hover:bg-[#174169] text-white" data-testid="save-batch">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
        {batches.length === 0 && <div className="text-sm text-[#64748B]">No batches yet.</div>}
        {batches.map((b) => (
          <div key={b.id} className="bg-white border border-[#E2E8F0] rounded-lg p-6 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] font-semibold">{b.course}</div>
                <h3 className="font-heading text-xl font-bold text-[#0F172A] mt-1">{b.name}</h3>
              </div>
              <button onClick={() => onDelete(b.id)} data-testid={`delete-batch-${b.id}`} className="p-2 hover:bg-[#FEF2F2] rounded">
                <Trash2 className="w-4 h-4 text-[#DC2626]" />
              </button>
            </div>
            <div className="mt-4 space-y-1.5 text-sm">
              <Row k="Trainer" v={b.trainer} />
              <Row k="Start" v={b.start_date} />
              <Row k="End" v={b.end_date || "—"} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between">
    <span className="text-[#64748B] text-xs uppercase tracking-wider">{k}</span>
    <span className="text-[#0F172A] font-medium">{v}</span>
  </div>
);

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-[#475569]">{label}{required && " *"}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="mt-1.5" />
    </div>
  );
}
